import type { CommandAction } from '../../types/CommandAction.js';
import type { LinkOptions } from './types/LinkOptions.js';
import { getSessionToLink } from './getSessionToLink.js';
import { createCloudpackClient } from '@ms-cloudpack/api-server';
import { formatLinkSummary } from './formatLinkSummary.js';
import { writeJson } from '@ms-cloudpack/json-utilities';
import path from 'path';
import { linkToRemoteSession } from './linkToRemoteSession.js';

/**
 * Defines the "link" verb entry point.
 */
export const execute: CommandAction<LinkOptions> = async (params) => {
  // Use the current directory as the default package path.
  const { options, appPath, reporter } = params;
  const {
    remote,
    ignore: ignoredPackages,
    all: includeAll,
    ignoreResolutions,
    resolveStrategy,
    logResolveMap,
    cachePath,
  } = options;

  console.debug('Linking packages...');
  console.debug('Include all:', !!includeAll);
  console.debug('Ignored packages:', ignoredPackages?.join(', ') || 'none');
  if (!ignoreResolutions || typeof ignoreResolutions === 'boolean') {
    console.debug('Apply host resolutions:', !ignoreResolutions);
  } else {
    console.debug('Ignoring resolutions:', ignoreResolutions.join(', '));
  }
  console.debug('Resolve strategy:', resolveStrategy);
  console.debug('Remote:', remote);

  // If we are linking to a remote session, we need to use the link proxy.
  if (remote) return linkToRemoteSession(params);

  // If we are linking to a session on the same machine, we can access the paths directly.
  const sessionResult = await getSessionToLink({ reporter, cachePath });
  if (!sessionResult.session) {
    return sessionResult;
  }
  const { session } = sessionResult;
  const client = await createCloudpackClient({ url: session.urls.apiServer, reporter });

  const includeMessage = includeAll ? ' including all internal paths' : '';
  const message = `Linking local paths for ${session.projectName} at "${appPath}"${includeMessage}`;
  const task = reporter.addTask(message);

  // Send request to api server to link paths.
  const { resolveMap, linkedPaths } = await client.linkPath.mutate({
    linkedPath: {
      path: appPath,
      ignoredPackages,
      includeAll,
      ignoreResolutions,
      resolveStrategy,
    },
  });
  await client.close();

  if (linkedPaths.find((p) => p.path === appPath) === undefined) {
    // Close down with an error.
    task.complete({ status: 'fail', message: `Failed to link paths.`, forceShow: true });
    return { hasErrors: true, message: 'Failed to link paths.' };
  }

  if (logResolveMap) {
    // Write the linked resolve map to disk.
    await writeJson(path.join(appPath, 'resolve-map-linked.json'), resolveMap);
  }

  // Close down successfully.
  task.complete({ status: 'complete', message: `Linked paths`, forceShow: true });
  const linkSummary = formatLinkSummary({ session, resolveMap, linkedPaths, resolveStrategy });
  return { message: linkSummary };
};
